// @ts-strict-ignore
import { CopiedArticleInformation } from '@project/components/article/model/copied-article-information';
import { OverlapInfo } from '@3d-scene/model/overlap-info';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleAttributeUtils } from '@article/util/article-attribute.utils';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArticleContainerPropertyDef } from '@article/constants/article-container-property-def';
import { CloneUtils } from '@shared/util/clone-utils';
import { ProjectType } from '../../../model/project-type';
import { StringUtils } from '@shared/util/string-utils';

export class CopiedRangeOnLineArticleInformation {
  public projectType: ProjectType;
  public rangeOnLineArticleType: string;

  public articlesInformation: CopiedArticleInformation[] = [];
  public articlesOnRailInformation: CopiedArticleInformation[] = [];

  public commonArticlesLength: number;
  public overlapInfo: OverlapInfo;

  public getArticlesInformation(): CopiedArticleInformation[] {
    return this.articlesInformation;
  }

  public getArticlesOnRailInformation(): CopiedArticleInformation[] {
    return this.articlesOnRailInformation;
  }

  public addArticleInformation(articleInfo: CopiedArticleInformation, isConnectedOnRail: boolean): void {
    if (!articleInfo) {
      return;
    }

    this.articlesInformation.push(articleInfo);
    if (isConnectedOnRail) {
      this.articlesOnRailInformation.push(articleInfo);
    }
  }

  public isArticleConnectedToRangeOnLine(articleUsageId: string): boolean {
    if (!articleUsageId) {
      return false;
    }

    for (const articleInfo of this.articlesOnRailInformation) {
      if (articleUsageId === articleInfo.articleUsageId) {
        return true;
      }
    }
    return false;
  }

  public getArticleInfoByArticleUsageId(articleUsageId: string): CopiedArticleInformation {
    if (!articleUsageId) {
      return null;
    }

    for (const articleInfo of this.articlesInformation) {
      if (articleUsageId === articleInfo.articleUsageId) {
        return articleInfo;
      }
    }
    return null;
  }

  private getArticleInfoByOldArticleUsageId(oldArticleUsageId: string): CopiedArticleInformation {
    if (!oldArticleUsageId) {
      return null;
    }

    for (const articleInfo of this.articlesInformation) {
      if (oldArticleUsageId === articleInfo.oldArticleUsageId) {
        return articleInfo;
      }
    }
    return null;
  }

  /**
   * Clone existing article usages with new usageId.
   */
  public updateArticleUsages(): void {
    this.cloneArticleUsages();
    const articleUsages: ArticleUsageVO[] = this.getArticleUsages();
    const processedUsageGroupIds: string[] = []; // to handle usages in which groupId was already replaced
    const processedCompositeUsageIds: string[] = []; // to handle usages in which compositeUsageId was already replaced
    for (const articleInfo of this.articlesInformation) {
      const newUsageId: string = StringUtils.createUuid();
      articleInfo.oldArticleUsageId = articleInfo.articleUsageId;
      ArticleUsageUtils.replaceArticleUsageId(articleUsages, articleInfo.articleUsageId, newUsageId);
      if (articleInfo.dynamicArticleInfo) {
        articleInfo.dynamicArticleInfo.guid = newUsageId;
      }

      if (articleInfo.articleUsageGroupId && processedUsageGroupIds.indexOf(articleInfo.articleUsageGroupId) === -1) {
        const newGroupId: string = StringUtils.createUuid();
        ArticleUsageUtils.replaceArticleUsageGroupId(articleUsages, articleInfo.articleUsageGroupId, newGroupId);
        processedUsageGroupIds.push(newGroupId);
      }

      const compositeUsageId: string = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(articleInfo.articleUsage);
      if (compositeUsageId && processedCompositeUsageIds.indexOf(compositeUsageId) === -1) {
        const newCompositeUsageId: string = StringUtils.createUuid();
        ArticleUsageUtils.replaceCompositeArticleUsageId(articleUsages, compositeUsageId, newCompositeUsageId);
        processedCompositeUsageIds.push(newCompositeUsageId);
      }
    }
    this.updateDynamicArticleInfo();
  }

  private updateDynamicArticleInfo(): void {
    for (const articleInfo of this.getDynamicArticleInfos()) {
      const articleGuidsBetweenAdapters: string[] = ArticleAttributeUtils.getArticlesGuidsBetweenAdaptersFromArticleInfo(
        articleInfo.dynamicArticleInfo
      );
      if (!articleGuidsBetweenAdapters) {
        continue;
      }

      const newArticleGuidsBetweenAdapters: string[] = [];
      for (const articleGuid of articleGuidsBetweenAdapters) {
        const articleInformation: CopiedArticleInformation = this.getArticleInfoByOldArticleUsageId(articleGuid);
        if (articleInformation) {
          newArticleGuidsBetweenAdapters.push(articleInformation.articleUsageId);
        }
      }
      ArticleAttributeUtils.setAttributeValueFromArticleInfo(
        articleInfo.dynamicArticleInfo,
        ArticleContainerPropertyDef.ARTICLES_GUIDS_BETWEEN_ADAPTERS,
        newArticleGuidsBetweenAdapters
      );
    }
  }

  public getDynamicArticleInfos(): CopiedArticleInformation[] {
    const dynamicArticleInfos: CopiedArticleInformation[] = [];
    for (const articleInfo of this.articlesInformation) {
      if (articleInfo.isDynamic) {
        dynamicArticleInfos.push(articleInfo);
      }
    }
    return dynamicArticleInfos;
  }

  private cloneArticleUsages(): void {
    for (const articleInfo of this.articlesInformation) {
      articleInfo.articleUsage = CloneUtils.cloneArticleUsageVO(articleInfo.articleUsage);
    }
  }

  public getArticleUsages(): ArticleUsageVO[] {
    const result: ArticleUsageVO[] = [];
    for (const articleInfo of this.articlesInformation) {
      result.push(articleInfo.articleUsage);
    }
    return result;
  }

  public getArticleUsagesOnRail(): ArticleUsageVO[] {
    const result: ArticleUsageVO[] = [];
    for (const articleInfo of this.articlesOnRailInformation) {
      result.push(articleInfo.articleUsage);
    }
    return result;
  }
}
